<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import {
  ENTITY_TYPE,
  BUSINESS_DOC_TYPE,
  ID_TYPE,
} from '@clearbanc/data-common-types';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import type { ComputedRef } from 'vue';
import { RouteNames } from '@/data/routes';
import { UserDocumentStatus } from '@/types/verification-types';
import { FileUpload } from '@/types/file-upload.types';
import { BusinessDetails } from '@/types/business.types';
import UploadBlock from '@/components/upload/UploadBlock.vue';
import DSButton from 'primevue/button';
import { useDocumentUploads } from '@/composables/useDocumentUploads';
import Message from 'primevue/message';
import StatusTable from '@/components/StatusTable.vue';

// -----
// Setup
// -----

const store = useStore();
const router = useRouter();

const {
  hasBusinessUploadedRequiredVerificationDocs,
  hasUserUploadedRequiredVerificationDocs,
} = useDocumentUploads();

// -------------------
// Computed Properties
// -------------------

// Interface definitions for upload filters and matchers
interface UploadFilter {
  entity: ENTITY_TYPE;
  type: BUSINESS_DOC_TYPE | ID_TYPE;
  businessId?: number;
  metaId?: string;
}

// More flexible matcher interface to accommodate different query patterns
interface UploadMatcher {
  entity?: ENTITY_TYPE;
  type?: BUSINESS_DOC_TYPE | ID_TYPE;
  metaId?: string;
  [key: string]: any; // Allow additional properties
}

const user: ComputedRef<UserResponseDto> = computed(() => store.getters.user);
const business: ComputedRef<BusinessDetails> = computed(
  () => store.getters.business,
);
const einDocuments: ComputedRef<FileUpload[]> = computed(
  () => store.getters.einDocuments,
);
const incorporationUploadMatcher: ComputedRef<UploadMatcher> = computed(
  () => store.getters.incorporationUploadMatcher,
);
const uploadsMatching: ComputedRef<(matcher: UploadMatcher) => FileUpload[]> =
  computed(() => store.getters.uploadsMatching);
const eligibleOwners: ComputedRef<UserResponseDto[]> = computed(
  () => store.getters.eligibleOwners,
);

const incorporationDocuments: ComputedRef<FileUpload[]> = computed(() => {
  return uploadsMatching.value(incorporationUploadMatcher.value);
});

const passportUploads: ComputedRef<FileUpload[]> = computed(() => {
  return store.getters.uploadsMatching({
    entity: ENTITY_TYPE.USER,
    type: ID_TYPE.PASSPORT,
    metaId: user.value.id,
  });
});

const driversLicenseUploads: ComputedRef<FileUpload[]> = computed(() => {
  return store.getters.uploadsMatching({
    entity: ENTITY_TYPE.USER,
    type: ID_TYPE.DRIVERS_LICENSE,
    metaId: user.value.id,
  });
});

const uploadFiltersCorpDocs: ComputedRef<UploadFilter> = computed(() => {
  return {
    entity: ENTITY_TYPE.BUSINESS,
    type: BUSINESS_DOC_TYPE.CORP_DOCS,
    businessId: business.value.id,
  };
});

const uploadFiltersEIN: ComputedRef<UploadFilter> = computed(() => {
  return {
    entity: ENTITY_TYPE.BUSINESS,
    type: BUSINESS_DOC_TYPE.EIN_DOC,
    businessId: business.value.id,
  };
});

const uploadFiltersPassport: ComputedRef<UploadFilter> = computed(() => {
  return {
    entity: ENTITY_TYPE.USER,
    type: ID_TYPE.PASSPORT,
    metaId: user.value.id,
  };
});

const uploadFiltersDriversLicense: ComputedRef<UploadFilter> = computed(() => {
  return {
    entity: ENTITY_TYPE.USER,
    type: ID_TYPE.DRIVERS_LICENSE,
    metaId: user.value.id,
  };
});

const isSubmitDisabled: ComputedRef<boolean> = computed(() => {
  return !(
    hasBusinessUploadedRequiredVerificationDocs.value &&
    hasUserUploadedRequiredVerificationDocs.value
  );
});

const usersDocumentStatus: ComputedRef<UserDocumentStatus[]> = computed(() => {
  return eligibleOwners.value
    .filter((userItem) => userItem.id !== user.value.id)
    .map((userItem: UserResponseDto) => ({
      name: userItem.fullName,
      documentStatus: checkUserDocumentStatus(userItem.id),
    }));
});

// -------
// Methods
// -------

const removeUpload = (uploadId: string) => {
  store.dispatchApiAction('DELETE_USER_UPLOAD', { id: uploadId });
};

const navigateToOwnersIndex = () => {
  router.push({ name: RouteNames.OWNERS.INDEX });
};

const checkUserDocumentStatus = (userId: string) => {
  // TODO SC-192431: Move this into the verification service classes file to centralize logic
  const validIdTypes = [
    ID_TYPE.PASSPORT,
    ID_TYPE.DRIVERS_LICENSE,
    ID_TYPE.ID,
    ID_TYPE.PASSPORT_OR_LICENSE,
  ];

  const userUploads = uploadsMatching.value({
    entity: ENTITY_TYPE.OWNER,
    metaId: userId,
  });

  const hasIdDocs = userUploads.some((upload: FileUpload) =>
    validIdTypes.includes(upload.type),
  );

  return hasIdDocs ? 'Complete' : 'Pending';
};
</script>

<template>
  <form class="c-form">
    <div class="c-group">
      <Message severity="error" :closable="false" :class="$style['message']">
        <h3>Upload Documents</h3>
        <p>
          We couldn't successfully verify your information, please upload these
          documents:
        </p>
      </Message>
    </div>

    <br /><br />

    <div class="c-group">
      <div class="c-group__header">
        <h3 class="c-group__header__title">Business Documents</h3>
      </div>
      <div class="c-form__row grid grid-cols-12">
        <div class="col-span-6 md:col-span-12">
          <UploadBlock
            :upload-filters="uploadFiltersCorpDocs"
            :display-filters="incorporationUploadMatcher"
            :files="incorporationDocuments"
            hiddenInputLabel="Articles of Incorporation"
            :file-remove="removeUpload"
          >
            <template #title>
              Upload a copy of the Articles of Incorporation
            </template>
            <template #description>
              Please provide the official document issued when your company was
              formed (e.g., Articles of Incorporation) <br /><br />
            </template>
          </UploadBlock>
        </div>
        <div class="col-span-6 md:col-span-12">
          <UploadBlock
            :upload-filters="uploadFiltersEIN"
            :files="einDocuments"
            :file-remove="removeUpload"
          >
            <template #title> Upload a copy of the EIN document </template>
            <template #description>
              Please upload the official EIN verification letter your business
              received from the IRS after applying for an EIN. You can do this
              now or anytime before funding.
              <a
                :style="{ display: 'inline-block' }"
                href="https://help.clear.co/s/article/Invoice-Funding-Eligibility-Requirements?language=en_US"
                >Learn more</a
              >
            </template>
          </UploadBlock>
        </div>
      </div>
    </div>

    <br /><br />

    <div class="c-group">
      <div class="c-group__header">
        <h3 class="c-group__header__title">Owner Documents</h3>
      </div>
      <div class="c-form__row grid grid-cols-12">
        <div class="col-span-6 md:col-span-12">
          <UploadBlock
            :upload-filters="uploadFiltersPassport"
            :files="passportUploads"
            :file-remove="removeUpload"
          >
            <template #title>Passport (front and back)</template>
          </UploadBlock>
        </div>
        <div class="col-span-6 md:col-span-12">
          <UploadBlock
            :upload-filters="uploadFiltersDriversLicense"
            :files="driversLicenseUploads"
            :file-remove="removeUpload"
          >
            <template #title>Driver's license (front and back)</template>
          </UploadBlock>
        </div>
      </div>
    </div>

    <div class="c-group" v-if="usersDocumentStatus.length">
      <div class="c-group__header">
        <h3 class="c-group__header__title">Others</h3>
        <p class="c-group__header__description">
          We're also requesting documents from other owners as we can't verify
          their information as well. To expedite the process you can let them
          know.
        </p>
      </div>
      <div class="c-form__row">
        <StatusTable :usersDocumentStatus="usersDocumentStatus" />
      </div>
    </div>

    <div class="c-form__controls" :style="{ flexWrap: 'wrap' }">
      <DSButton
        label="Back"
        class="p-button-outlined"
        @click="navigateToOwnersIndex"
      />
      <DSButton
        label="Submit"
        :disabled="isSubmitDisabled"
        @click="navigateToOwnersIndex"
      />
    </div>
  </form>
</template>

<style module lang="less">
div.message {
  margin: 0;
}
</style>
